/** Ratio thresholds when a monthly allowance is available. */
export const CREDIT_ALLOWANCE_LOW_RATIO = 0.1;
export const CREDIT_ALLOWANCE_MEDIUM_RATIO = 0.25;

/** Absolute thresholds when no monthly allowance is returned. */
export const CREDIT_ABSOLUTE_LOW = 50;
export const CREDIT_ABSOLUTE_MEDIUM = 150;

/** Segments in the compact dotted credits indicator. */
export const CREDIT_DOTTED_SEGMENT_COUNT = 20;

export const CREDITS_INFO_TOOLTIP =
  "Credits are used for AI enrichment, email discovery and verification, and other credit-based actions in AlphaSearch.";

export const CREDIT_USAGE_STATUS = {
  HEALTHY: "healthy",
  MEDIUM: "medium",
  LOW: "low",
  EXHAUSTED: "exhausted",
};

/** Healthy credit indicator — matches token badge copy green (`AI_CREDIT_CHIP`). */
export const CREDIT_HEALTHY_COLOR = "#4caf50";

function numberOrNull(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function profileRoleName(profile) {
  return typeof profile?.role === "object" ? profile.role.role_name : null;
}

export function getTrialProfileMetadata(profile) {
  const hasTrialHistory = Boolean(profile?.trial_started_at);
  const isTrialUser = profileRoleName(profile) === "freemium" || hasTrialHistory;
  if (!profile || profile.is_verified === false || profile.trial_converted_at || !isTrialUser) {
    return { trialAllowance: null, trialExpiresAt: null };
  }

  return {
    trialAllowance: numberOrNull(profile.trial_credit_allowance),
    trialExpiresAt: profile.trial_expires_at || null,
  };
}

export function getTrialDaysRemaining(trialExpiresAt, now = Date.now()) {
  const expiresAt = Date.parse(trialExpiresAt);
  if (!Number.isFinite(expiresAt) || expiresAt <= now) return null;
  return Math.max(1, Math.ceil((expiresAt - now) / DAY_MS));
}

export function getNextTrialDayBoundaryDelay(trialExpiresAt, now = Date.now()) {
  const expiresAt = Date.parse(trialExpiresAt);
  if (!Number.isFinite(expiresAt) || expiresAt <= now) return null;
  const remaining = expiresAt - now;
  const displayedDays = Math.ceil(remaining / DAY_MS);
  return Math.max(1, remaining - ((displayedDays - 1) * DAY_MS));
}

/**
 * Normalize the balance API payload into a stable shape for UI consumers.
 * @param {Record<string, unknown> | null | undefined} data
 */
export function parseCreditBalanceResponse(data) {
  if (!data || typeof data !== "object") {
    return {
      totalAvailable: null,
      monthlyCredits: null,
      standaloneCredits: null,
      monthlyAllowance: null,
      monthlyResetAt: null,
    };
  }

  const totalAvailable =
    numberOrNull(data.balance) ??
    numberOrNull(data.total) ??
    (() => {
      const monthly = numberOrNull(data.monthly_credits);
      const standalone = numberOrNull(data.standalone_credits);
      if (monthly === null && standalone === null) return null;
      return (monthly ?? 0) + (standalone ?? 0);
    })();

  return {
    totalAvailable,
    monthlyCredits: numberOrNull(data.monthly_credits),
    standaloneCredits: numberOrNull(data.standalone_credits),
    monthlyAllowance: numberOrNull(data.monthly_credit_allowance),
    monthlyResetAt: data.monthly_credit_reset_at || null,
  };
}

/**
 * Remaining credits as a 0–1 ratio against the best available allowance signal.
 */
export function getCreditRemainingRatio({ totalAvailable, monthlyCredits, monthlyAllowance }) {
  if (totalAvailable === null) return null;
  if (monthlyAllowance !== null && monthlyAllowance > 0) {
    if (totalAvailable <= 0) return 0;
    const allowanceBalance = monthlyCredits !== null && monthlyCredits !== undefined
      ? monthlyCredits
      : totalAvailable;
    return Math.max(0, Math.min(1, allowanceBalance / monthlyAllowance));
  }
  return null;
}

export function getCreditUsageStatus({ totalAvailable, monthlyCredits, monthlyAllowance }) {
  if (totalAvailable === null) return null;
  if (totalAvailable <= 0) return CREDIT_USAGE_STATUS.EXHAUSTED;

  const ratio = getCreditRemainingRatio({ totalAvailable, monthlyCredits, monthlyAllowance });

  if (monthlyAllowance !== null && monthlyAllowance > 0) {
    if (ratio <= CREDIT_ALLOWANCE_LOW_RATIO) return CREDIT_USAGE_STATUS.LOW;
    if (ratio <= CREDIT_ALLOWANCE_MEDIUM_RATIO) return CREDIT_USAGE_STATUS.MEDIUM;
    return CREDIT_USAGE_STATUS.HEALTHY;
  }

  if (totalAvailable <= CREDIT_ABSOLUTE_LOW) return CREDIT_USAGE_STATUS.LOW;
  if (totalAvailable <= CREDIT_ABSOLUTE_MEDIUM) return CREDIT_USAGE_STATUS.MEDIUM;
  return CREDIT_USAGE_STATUS.HEALTHY;
}

/**
 * Maps credit usage status to design-system semantic colors:
 * healthy → sage green, medium → yellow, low/exhausted → red.
 * Used by the avatar ring and legacy indicators.
 * @param {{ warning: { main: string }, error: { main: string } }} palette
 */
export function getCreditUsageStatusColor(palette, status) {
  switch (status) {
    case CREDIT_USAGE_STATUS.EXHAUSTED:
    case CREDIT_USAGE_STATUS.LOW:
      return palette.error.main;
    case CREDIT_USAGE_STATUS.MEDIUM:
      return palette.warning.main;
    case CREDIT_USAGE_STATUS.HEALTHY:
    default:
      return CREDIT_HEALTHY_COLOR;
  }
}

export function formatCreditCount(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return value.toLocaleString();
}

export function buildCreditBalanceViewModel(data, { profile = null, now = Date.now() } = {}) {
  const parsed = parseCreditBalanceResponse(data);
  const { trialAllowance, trialExpiresAt } = getTrialProfileMetadata(profile);
  const hasSubscriptionAllowance = parsed.monthlyAllowance !== null && parsed.monthlyAllowance > 0;
  const effectiveAllowance = hasSubscriptionAllowance ? parsed.monthlyAllowance : trialAllowance;
  const ratioInput = {
    ...parsed,
    monthlyCredits: hasSubscriptionAllowance ? parsed.monthlyCredits : null,
    monthlyAllowance: effectiveAllowance,
  };
  const remainingRatio = getCreditRemainingRatio(ratioInput);
  const status = getCreditUsageStatus(ratioInput);

  return {
    ...parsed,
    effectiveAllowance,
    remainingRatio,
    status,
    trialAllowance: hasSubscriptionAllowance ? null : trialAllowance,
    trialExpiresAt: hasSubscriptionAllowance ? null : trialExpiresAt,
    trialDaysRemaining: hasSubscriptionAllowance
      ? null
      : getTrialDaysRemaining(trialExpiresAt, now),
    remainingLabel:
      parsed.totalAvailable === null
        ? null
        : `${formatCreditCount(parsed.totalAvailable)} left`,
  };
}

/**
 * Returns filled/inactive dot counts for the segmented credits indicator.
 * @param {number} remainingRatio 0–1
 * @param {number} [segmentCount]
 */
export function getCreditDottedSegmentCounts(remainingRatio, segmentCount = CREDIT_DOTTED_SEGMENT_COUNT) {
  const safeRatio = Math.max(0, Math.min(1, Number(remainingRatio) || 0));
  const filled = Math.round(safeRatio * segmentCount);
  return {
    filled,
    inactive: Math.max(0, segmentCount - filled),
    segmentCount,
  };
}
